import { z } from "zod";
import { isPrescriptionTransfer } from "./health-profile-summary";
import { medicalEvidenceTaskKeySchema } from "./medical-evidence";
import type { BmiQuizAnswerValue } from "./bmi-quiz";

// Which of the two locked journeys a patient is on.
//
// The two are genuinely different products, not variations of one:
//
//   NEW_TREATMENT        F-01 → booking + payment → F-02 → consultation →
//                        CP-06 → review → decision → subscription →
//                        prescription → actual first dose → DT-01
//
//   PRESCRIPTION_TRANSFER F-01 (active choice) → routing block in the
//                        feedback → F-02 → prep page → F-07 (treatment
//                        details, prescription evidence, blood pressure/
//                        resting pulse) → prescription_transfer_review_pending
//                        → asynchronous review → decision → subscription →
//                        transfer/maintenance (TES-733, Emelie 2026-09-16)
//
// A transfer never books a consultation, never pays a meeting fee, and never
// reports a first dose — she is already on treatment. Everything that assumes
// otherwise (CP-01, CP-12, DT-01) has to be able to ask which journey applies.
export const PATIENT_GROUPS = [
  "NEW_TREATMENT",
  "PRESCRIPTION_TRANSFER",
] as const;
export const patientGroupSchema = z.enum(PATIENT_GROUPS);
export type PatientGroup = z.infer<typeof patientGroupSchema>;

/**
 * The patient's journey, derived from her own F-01 answer.
 *
 * This is the derivation ONLY. Since TES-852 its result is stored — the API
 * freezes it on `Patient.patientGroup` the first time F-01 answers, because
 * Emelie's section 1 requires that later forms cannot overwrite it. This
 * function is therefore called once per patient in the normal case, not on
 * every read; `resolvePatientGroup` owns when.
 *
 * A client still can never assert its own group — the same reasoning as
 * `formContext`.
 *
 * No F-01 answers yet means NEW_TREATMENT: that is the default journey, and
 * a patient who has not said she wants to transfer a prescription has not
 * asked for one. Note that callers must NOT persist that default for a
 * patient who has not answered — see establishPatientGroup for why.
 */
export function derivePatientGroup(
  answers: Record<string, BmiQuizAnswerValue> | null | undefined,
): PatientGroup {
  if (!answers) return "NEW_TREATMENT";
  // TES-733 — the track is on (LAUNCH.prescriptionTransferTrack); the
  // group is her own active choice in F-01 and nothing else.
  return isPrescriptionTransfer(answers)
    ? "PRESCRIPTION_TRANSFER"
    : "NEW_TREATMENT";
}

/**
 * Which patient groups a flow is for.
 *
 * Stored on the flow (`authorMetadata.audience`) so a locked rule is a
 * content change, not a code change. Absent means "everyone", which keeps
 * every existing flow behaving exactly as before.
 */
export const flowAudienceSchema = z.object({
  patient_group: z.array(patientGroupSchema).min(1).optional(),
});
export type FlowAudience = z.infer<typeof flowAudienceSchema>;

/**
 * Does this flow apply to this patient?
 *
 * Fail-OPEN on a malformed or absent audience: a flow with no valid rule
 * reaches everyone, exactly as it did before audiences existed. Fail-closed
 * would silently withhold care content the moment someone typos a group name
 * — the same class of mistake as the recipe guard that rejected every plan.
 */
export function flowAppliesTo(audience: unknown, group: PatientGroup): boolean {
  const parsed = flowAudienceSchema.safeParse(audience);
  if (!parsed.success) return true;
  const groups = parsed.data.patient_group;
  if (!groups || groups.length === 0) return true;
  return groups.includes(group);
}

// ---------------------------------------------------------------------------
// TES-760 — the doctor's late transition
// ---------------------------------------------------------------------------

/**
 * Moving a patient's case between the two journeys after the fact
 * (Emelie 2026-09-17, reopened and widened 2026-09-22).
 *
 * The derivation above reads her own F-01 answer, which is right for what SHE
 * said. But if she started treatment elsewhere AFTER answering — or simply
 * mis-tapped the question — no reading of those answers can reach that
 * conclusion; only a clinician can. So the decision is stored and wins over
 * the derivation.
 *
 * TES-862 — BIDIRECTIONAL since Ö-209. The paragraph that stood here said the
 * correction was one-directional "because going back is a different decision
 * nobody has taken". It has been taken now:
 *
 *   "Den läkarstyrda korrigeringen ska vara dubbelriktad."
 *   NEW_TREATMENT → PRESCRIPTION_TRANSFER, and back again.
 *
 * What did NOT change is who may take it. The patient still cannot move
 * herself by resubmitting a later form — TES-852 freezes the group at F-01
 * precisely so she cannot, and Ö-209 restates it:
 *   "Vid felklick kontaktar patienten support/vårdteamet och Kirill"
 *   "korrigerar i admin."
 *
 * The old worry (a reversal strands the review, the evidence and the waiver)
 * is answered by keeping everything rather than by refusing to reverse: the
 * correction preserves every submission, upload, decision and completed step,
 * closes only what the new group cannot be an audience for, and routes to the
 * next MISSING real step. See `careCaseRoutingSchema` below.
 */
export const CARE_CASE_TRANSITIONS = [
  "NEW_TREATMENT_TO_PRESCRIPTION_TRANSFER",
  "PRESCRIPTION_TRANSFER_TO_NEW_TREATMENT",
] as const;
export const careCaseTransitionSchema = z.enum(CARE_CASE_TRANSITIONS);
export type CareCaseTransition = z.infer<typeof careCaseTransitionSchema>;

/** Where each transition comes from, and where it lands. */
export const CARE_CASE_TRANSITION_FROM: Record<
  CareCaseTransition,
  PatientGroup
> = {
  NEW_TREATMENT_TO_PRESCRIPTION_TRANSFER: "NEW_TREATMENT",
  PRESCRIPTION_TRANSFER_TO_NEW_TREATMENT: "PRESCRIPTION_TRANSFER",
};
export const CARE_CASE_TRANSITION_TO: Record<CareCaseTransition, PatientGroup> =
  {
    NEW_TREATMENT_TO_PRESCRIPTION_TRANSFER: "PRESCRIPTION_TRANSFER",
    PRESCRIPTION_TRANSFER_TO_NEW_TREATMENT: "NEW_TREATMENT",
  };

/**
 * WHY the case was corrected, as a value rather than as prose. Ö-209:
 * "Varje korrigering kräver strukturerad ändringsorsak, kompletterande"
 * "fritext där det behövs, ansvarig användare, tidpunkt, auditlogg."
 *
 * Free text alone was what TES-760 stored, and it cannot be counted, filtered
 * or audited — "hon hade redan behandling" and "startat hos annan vårdgivare"
 * are the same fact written twice. The structured code is what a later reader
 * (and the next clinician) can act on; `reasonText` carries the particulars.
 *
 * The list is deliberately short and covers both directions. `other` is the
 * escape hatch and is the one code that REQUIRES the free text — an "other"
 * with nothing written is a correction with no reason at all.
 */
export const CARE_CASE_CHANGE_REASONS = [
  // She began treatment with another provider after answering F-01 — the
  // original TES-760 case, and the one the derivation can never reach.
  "treatment_started_elsewhere",
  // The felklick Ö-209 names: her F-01 answer does not match her situation.
  "patient_answered_incorrectly",
  // Nothing to take over: no valid prescription or ongoing order could be
  // established, so she belongs on the new-treatment journey.
  "no_ongoing_prescription",
  // The clinician reassessed the case on the evidence in front of him.
  "clinical_reassessment",
  "other",
] as const;
export const careCaseChangeReasonSchema = z.enum(CARE_CASE_CHANGE_REASONS);
export type CareCaseChangeReason = z.infer<typeof careCaseChangeReasonSchema>;

export const transitionCareCaseSchema = z
  .object({
    transition: careCaseTransitionSchema,
    // The structured "why". Required: this overrides what the patient herself
    // answered, and a correction whose reason is unreadable to the next
    // clinician is a correction nobody can review.
    reasonCode: careCaseChangeReasonSchema,
    // The particulars, where a code cannot carry them. Optional except for
    // `other` — see the superRefine.
    reasonText: z.string().trim().max(2000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.reasonCode === "other" && !v.reasonText) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reasonText"],
        message: "reason_text_required",
      });
    }
  });
export type TransitionCareCaseRequest = z.infer<
  typeof transitionCareCaseSchema
>;

// ---------------------------------------------------------------------------
// TES-862 / Ö-210 — what the patient's next task is after a correction
// ---------------------------------------------------------------------------

/**
 * The next REAL step, decided from what the case already contains.
 *
 * Emelie 2026-09-22, Ö-210:
 * "Systemet ska skapa nästa saknade riktiga steg, inte starta om onboarding"
 * "eller automatiskt kräva ett nytt möte som redan har ägt rum."
 * So none of these can mean "start over": each one names a step that is
 * genuinely missing, and a step already taken is never named.
 *
 * Towards PRESCRIPTION_TRANSFER her three lettered cases, in order:
 *
 *   A `transfer_form_required`       mandatory F-07 answers about the current
 *                                    treatment/prescription are missing ⇒
 *                                    open F-07.
 *   B `targeted_completion_required` the main data is there but one or more
 *                                    exact pieces of evidence are not ⇒ the
 *                                    doctor picks the matching CP-06 tasks
 *                                    (`missingEvidenceTasks`) instead of
 *                                    sending her through F-07 again.
 *   C `transfer_review_ready`        everything required is in ⇒ straight to
 *                                    the prescription-transfer review.
 *
 * Towards NEW_TREATMENT, the same principle read backwards — the consultation
 * ladder's first step she has NOT taken:
 *
 *   `intake_required`          no clean F-02 on file.
 *   `meeting_booking_required` F-02 is in, but no meeting is booked or held.
 *   `meeting_scheduled`        a booked meeting stands. Wait for it; do not
 *                              ask her to book a second one.
 *   `post_meeting_review`      the meeting has HAPPENED and is documented.
 *                              Nothing is owed by her; the case is the
 *                              doctor's (CP-06 / the treatment decision).
 */
export const CARE_CASE_NEXT_STEPS = [
  "transfer_form_required",
  "targeted_completion_required",
  "transfer_review_ready",
  "intake_required",
  "meeting_booking_required",
  "meeting_scheduled",
  "post_meeting_review",
] as const;
export const careCaseNextStepSchema = z.enum(CARE_CASE_NEXT_STEPS);
export type CareCaseNextStep = z.infer<typeof careCaseNextStepSchema>;

export const careCaseRoutingSchema = z.object({
  nextStep: careCaseNextStepSchema,
  /**
   * Case B only: the CP-06 tasks the doctor should pick from, because F-07
   * has not delivered them. Empty for every other step — an empty list is
   * "nothing to ask her for", never "we did not look".
   */
  missingEvidenceTasks: z.array(medicalEvidenceTaskKeySchema),
  /** What the correction withdrew, for the readback the doctor sees. */
  closedAssignmentIds: z.array(z.string()),
  /**
   * Case C only: the review the correction opened (or the open one it found).
   * Null whenever the routing did not land on the review.
   */
  transferReviewId: z.string().nullable(),
});
export type CareCaseRouting = z.infer<typeof careCaseRoutingSchema>;

export const careCaseTransitionResultSchema = z.object({
  patientId: z.string(),
  patientGroup: patientGroupSchema,
  transitionedAt: z.string(),
  transitionedBy: z.string(),
  reasonCode: careCaseChangeReasonSchema,
  routing: careCaseRoutingSchema,
});
export type CareCaseTransitionResult = z.infer<
  typeof careCaseTransitionResultSchema
>;
